<template>
  <div :class="`MainVerticalMenu ${toggleOpen && !hasContextMenu ? 'open' : ''}`">
    <div ref="start" class="MainVerticalMenu-start">
      <div
        v-for="(item, index) in items"
        :key="item.label"
        @click="
          item.label == labelPorteur
            ? clickOnItem(item)
            : () => {
                return false
              }
        "
      >
        <router-link
          :ref="`item${index}`"
          class="MainVerticalMenu-item"
          :to="{ path: item.toPath }"
          :class="{ 'router-link-active': item.isLinkActive }"
        >
          <span v-if="item.hasCustomIcon" class="icon is-large custom-icon">
            <span :style="getCustomIconStyle(item)"></span>
          </span>
          <b-icon v-else size="is-large" :icon="item.icon" />
          <span v-if="toggleOpen && !hasContextMenu" class="content-label">
            {{ item.label }}
            <span v-if="shouldDisplayBadge(item.label)" class="badge">{{
              pendingRequest.users
            }}</span>
            <span v-if="isDisplayPorteurProjet(item.label)" class="badge porteur decalage">{{
              pendingRequest.modif_porteur
            }}</span>
          </span>
        </router-link>
      </div>
    </div>
    <div class="MainVerticalMenu-end">
      <FeedbackModal class="MainVerticalMenu-feedback" />
      <div
        v-show="!hasContextMenu"
        class="MainVerticalMenu-arrow-button clickable"
        @click="toggleOpenMenu"
      >
        <b-icon
          size="is-normal"
          :icon="`${toggleOpen ? 'angle-double-left' : 'angle-double-right'}`"
        ></b-icon>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import FeedbackModal from "@/views/feedback/FeedbackModal.vue"
import { NavbarItem } from "@/views/menus/MenuItems"
import { t } from "@/utils/i18n-utils"
import { getIconUrl } from "@/utils/asset-utils"
import { BIcon } from "buefy"

export default defineComponent({
  name: "MainVerticalMenu",
  components: {
    FeedbackModal,
    BIcon,
  },
  props: {
    items: {
      type: Array as PropType<NavbarItem[]>,
      required: false,
      default: () => [],
    },
    hasContextMenu: {
      type: Boolean,
      required: false,
    },
    menuExpanded: {
      type: Boolean,
      required: false,
    },
    pendingRequest: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    role: {
      type: String,
      required: false,
      default: "",
    },
  },

  emits: ["main-menu-expand", "click-on-item", "reset-notif-porteur"],
  data() {
    return {
      toggleOpen: true,
      labelPorteur: t("menu.ModifProjet"),
    }
  },
  watch: {
    menuExpanded(): void {
      this.toggleOpen = this.menuExpanded
    },
  },
  methods: {
    toggleOpenMenu(): void {
      this.toggleOpen = !this.toggleOpen
      this.$emit("main-menu-expand", this.toggleOpen)
    },

    clickOnItem(item: NavbarItem): void {
      this.$emit("click-on-item", item)

      if (this.labelPorteur == item.label) {
        this.$emit("reset-notif-porteur")
      }
    },

    shouldDisplayBadge(label: string): boolean {
      if (this.role === "ANIMATEUR_REGIONAL" && label === "Mon GRAE") {
        return this.pendingRequest.users > 0
      }
      if (this.role === "ACCOMPAGNATEUR_NATIONAL" && label === "Utilisateurs") {
        return this.pendingRequest.users > 0
      }
      return false
    },

    isDisplayPorteurProjet(label: string): boolean {
      let display = false
      if (this.labelPorteur == label) {
        display = this.pendingRequest.modif_porteur > 0
      }
      return display
    },

    getCustomIconStyle(navbarItem: NavbarItem): Record<string, string> {
      const iconUrl = getIconUrl(navbarItem.icon)
      return {
        maskImage: `url("${iconUrl}")`,
        WebkitMaskImage: `url("${iconUrl}")`,
      }
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

$active-border-width: 4px;

.MainVerticalMenu {
  background-color: $main-vertical-menu-bg-color;
  width: $main-vertical-menu-width;
  height: 100%;
  box-shadow: 2px 0 0 0 whitesmoke;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .MainVerticalMenu-start {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .MainVerticalMenu-item {
    color: $text-light-color;
    height: $main-vertical-menu-width;
    border-bottom: 1px solid #3c3c3c;
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .icon {
      color: $main-vertical-menu-select-color;
      margin: 0;
      width: $main-vertical-menu-width;
      height: $main-vertical-menu-width;
    }

    .content-label {
      position: relative;
    }

    &.router-link-active {
      background-color: $main-vertical-menu-active-bg-color;
      border-left: solid $active-border-width $main-vertical-menu-select-color;

      .icon {
        margin-left: -$active-border-width;
      }
    }

    &:hover {
      background-color: $main-vertical-menu-select-color;
      color: $main-vertical-menu-bg-color;

      .icon {
        color: $main-vertical-menu-bg-color;
      }

      &.router-link-active {
        border-left: solid $active-border-width white;
      }
    }
  }

  .MainVerticalMenu-end {
    position: relative;
    flex-direction: column;
  }
}

.MainVerticalMenu-arrow-button {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .icon {
    width: $main-vertical-menu-width;
    height: $main-vertical-menu-width / 2;
    color: $text-light-color;
    background-color: $ofb-bleu-2;
  }
}

.MainVerticalMenu-feedback {
  margin-bottom: 1.5rem;
  margin-left: calc((#{$main-vertical-menu-width} - #{$feedback-button-width}) / 2);
}

.MainVerticalMenu.open {
  width: $main-main-menu-expand-width;
}

.badge.porteur.decalage {
  right: 10px;
  top: 15px;
}

span.custom-icon > span {
  display: inline-block;
  width: 50%;
  height: 50%;
  background-color: $main-vertical-menu-select-color;
  mask-size: cover;
  mask-repeat: no-repeat;
  mask-position: center;
}

.MainVerticalMenu-start > div:hover span.custom-icon > span {
  background-color: $main-vertical-menu-active-bg-color;
}
</style>
